// Administration › Sales — the CEO view of revenue.
//
// Every dollar that reached a Salesforce Order, split the way the business
// actually earns it: Web (customer self-serve checkout), Chat (the front-of-site
// AI assistant put a line in the cart), MCP (the customer's own AI assistant —
// Claude, ChatGPT, Cursor — bought it over the Squish MCP server), Rep (a staffer placed it — MOTO, staff
// transfer), Renewal (Stripe subscription auto-renewal), and Unattributed
// (orders from before the Channel__c field existed, until the backfill runs).
// Chat is its own lane because the assistant is the one channel with a running
// per-token cost: folded into Web there is no way to ask whether it pays for
// itself. It only exists on orders placed from 2026-08-25 on; MCP from
// 2026-10-28 on. Plus the month by rep — revenue, MARGIN and the bonus on the
// margin — and the latest orders with who took them.
//
// Revenue and margin are both shown, never one standing in for the other. A
// $33 domain and a $33 hosting month are the same revenue and nothing like the
// same business, which is why the bonus moved to margin in the first place.
// Margin is null (—) on orders stamped before the field existed; that is "not
// measured", not "made nothing", and the page says so by not drawing a zero.
//
// REVENUE IS NET OF REFUNDS (Casey, 2026-10-12), and a refund counts against
// the month of the original sale. The API sends each revenue figure net, with
// its gross and refunded amount beside it, and `refunds: 'unavailable'` when
// the refunded amount could not be read — in which case the figures are gross
// and this page says so on the strip rather than passing them off as net. The
// wording lives in lib/netRevenue.js. Margin and the bonus are not affected:
// margin was already re-stamped on refund.
//
// The Dashboard's top strip is deliberately NOT this: that is "my work",
// scoped to whoever is logged in. This is everybody's. Reads
// GET /api/admin/sales (admin only); the backend degrades to available:false
// when Order.Channel__c / Placed_By__c aren't readable — see
// squish-docs/runbooks/deploy-order-attribution.md.

import React, { useEffect, useState, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { getHeaders } from '../api';
import { formatSfDate } from '../lib/dates';
import { refundsState, netLabel, netLabelTitle, refundedNote, orderAmount, money as netMoney } from '../lib/netRevenue';

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

// Channel palette. Web is the accent (it is the business), Chat a distinct cyan
// so it reads as its own thing beside Web rather than a shade of it, MCP pink
// for the same reason beside Chat, Rep the money green, Renewal the quiet base,
// Unattributed a warning — it should go away. Keep these keys in step with CHANNELS in squish-crm-backend
// lib/sales-attribution.js: a value the API returns and this map omits draws a
// bar with no colour and a KPI with no label.
const CHANNEL_COLOR = {
  Web: '#7c6dfa',
  Chat: '#4bc8e6',
  MCP: '#f07ccf',
  Rep: '#6dfabc',
  Renewal: '#9090a8',
  Unattributed: '#fac850',
};
const CHANNEL_ORDER = ['Web', 'Chat', 'MCP', 'Rep', 'Renewal', 'Unattributed'];
const CHANNEL_HINT = {
  Web: 'customer self-serve checkout',
  Chat: 'the AI assistant added it to the cart',
  MCP: "the customer's own AI assistant bought it over MCP",
  Rep: 'placed by a staffer',
  Renewal: 'Stripe auto-renewals',
  Unattributed: 'not yet classified',
};
// Labels that are not just the raw key. "Chat" alone reads like a support
// channel on a page about revenue.
const CHANNEL_LABEL = { Chat: 'AI Chat' };

function fmt$(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmt$c(v) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  if (Math.abs(n) >= 10000) return `$${(n / 1000).toFixed(1)}k`;
  // Cents below $100 only: seven tiles share the strip now, and ".00" on a
  // four-figure number is what pushes the value past its cell.
  if (Math.abs(n) >= 100) return `$${Math.round(n).toLocaleString('en-US')}`;
  return fmt$(n);
}
function axisMoney(v) {
  if (Math.abs(v) >= 10000) return `$${(v / 1000).toFixed(0)}k`;
  if (Math.abs(v) >= 1000)  return `$${(v / 1000).toFixed(1)}k`;
  return `$${Math.round(v)}`;
}
// This page had the UTC-safe version first; it now shares it, and also stops
// producing "Invalid Date" for a DateTime value (the old split() only handled
// bare calendar days). See lib/dates.js.
const fmtDate = (d) => formatSfDate(d, { year: false });
function pct(part, whole) {
  if (!whole) return '—';
  return `${Math.round((part / whole) * 100)}%`;
}

// Margin as a share of the revenue that produced it. Null-safe: an unstamped
// figure has no percentage, and 0% would be a claim we cannot make.
function marginPct(margin, revenue) {
  if (margin == null || !revenue) return null;
  return `${Math.round((margin / revenue) * 100)}% margin`;
}

// The small "−$X refunded" under a net amount in a table cell. Same treatment
// as the account page's order table, so a refund looks the same everywhere.
function RefundedLine({ amount }) {
  if (!(amount > 0)) return null;
  return <div style={{ fontSize: 10, color: 'var(--badge-yellow)', fontFamily: 'var(--mono)', fontWeight: 400 }}>{'−'}{netMoney(amount)} refunded</div>;
}

// `title` is the tile's hover text — where a net figure says what it is net of.
function Kpi({ label, value, tone, sub, subTone, highlight, title }) {
  return (
    <div className={`kpi${highlight ? ' kpi-hi' : ''}`} title={title || undefined}>
      <div className="kpi-label">{label}</div>
      <div className={`kpi-value${tone ? ` ${tone}` : ''}`}>{value}</div>
      <div className={`kpi-sub${subTone ? ` ${subTone}` : ''}`}>{sub || ' '}</div>
    </div>
  );
}

export const TrendTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const rows = payload.filter(p => p.value > 0);
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  // The month's own row: `refunded` is what came off that month's sales
  // (null when refunds are unavailable, undefined from an older backend).
  const refunded = payload[0]?.payload?.refunded;
  return (
    <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontFamily: 'var(--mono)', fontSize: 12 }}>
      <div style={{ color: 'var(--text2)', marginBottom: 6 }}>{label} · <span style={{ color: 'var(--text)', fontWeight: 700 }}>{fmt$(total)}</span></div>
      {rows.map(p => (
        <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 18, color: 'var(--text2)' }}>
          <span><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: p.fill, marginRight: 6 }} />{p.dataKey}</span>
          <span style={{ color: 'var(--text)' }}>{fmt$(p.value)}</span>
        </div>
      ))}
      {refunded > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18, color: 'var(--badge-yellow)', marginTop: 4 }}>
          <span>refunded</span><span>{'−'}{netMoney(refunded)}</span>
        </div>
      )}
    </div>
  );
};

export default function Sales({ onPickCustomer }) {
  const [months, setMonths] = useState(6);
  const [data, setData]     = useState(null);
  const [error, setError]   = useState('');
  const [loading, setLoad]  = useState(true);

  const load = useCallback(() => {
    setLoad(true); setError('');
    return fetch(`${API}/api/admin/sales?months=${months}&limit=25`, { headers: getHeaders() })
      .then(async r => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.error || `HTTP ${r.status}`);
        return body;
      })
      .then(d => setData(d))
      .catch(e => setError(e.message))
      .finally(() => setLoad(false));
  }, [months]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>Sales</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>
            Every activated order, all channels{data?.asOf ? ` · as of ${fmtDate(data.asOf)}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className="tab-bar">
            {[3, 6, 12].map(m => (
              <button key={m} className={`tab-btn${months === m ? ' is-active' : ''}`} onClick={() => setMonths(m)}>{m} mo</button>
            ))}
          </div>
          <button className="tab-refresh" onClick={load}>refresh</button>
        </div>
      </div>

      {error && <div className="error-state">⚠ {error}</div>}
      {loading && !data && <div className="loading-state"><span className="loading-dot" /><span className="loading-dot" /><span className="loading-dot" /></div>}

      {data && !data.available && (
        <div className="table-card" style={{ padding: 20 }}>
          <div style={{ fontSize: 13, color: 'var(--badge-yellow)', fontFamily: 'var(--mono)', lineHeight: 1.6 }}>
            Not being measured yet — the Order attribution fields aren't readable for the CRM's Salesforce user.
            <div style={{ color: 'var(--text3)', marginTop: 4 }}>{data.reason}</div>
          </div>
        </div>
      )}

      {data?.available && <SalesReport data={data} onPickCustomer={onPickCustomer} />}
    </div>
  );
}

// The report itself, as a pure view of one GET /api/admin/sales payload — so
// what the page says about a payload can be rendered and read in a test
// (test/net-revenue-pages.test.js) without a browser or a fetch. The JSX below
// keeps the indentation it had inside Sales() so this extraction reads as the
// change it is.
export function SalesReport({ data, onPickCustomer }) {
  const mtd = data?.mtd;
  const byChannel = mtd?.byChannel || {};
  const marginByChannel = mtd?.marginByChannel || {};
  const bonusPct = ((data?.bonusPct ?? 0) * 100).toFixed(0);
  // Only chart the channels that have any revenue in the window; four legend
  // entries for two lines of data is noise. Unattributed is always shown while
  // it is non-zero — the point of the yellow is that it should bother you.
  const activeChannels = CHANNEL_ORDER.filter(c => (data?.trend || []).some(t => (t[c] || 0) > 0));
  const trendTotal = (data?.trend || []).reduce((s, t) => s + (t.total || 0), 0);
  const repTotalBonus = (data?.byRep || []).reduce((s, r) => s + (r.bonus || 0), 0);

  // Net, gross, or a backend that never said. Drives one phrase on the scope
  // line and on the chart, and the "−$X refunded" sublines; see lib/netRevenue.
  const refunds = refundsState(data);
  const label = netLabel(refunds);
  const labelTitle = netLabelTitle(refunds, data?.refundsReason);
  const mtdNote = refundedNote({ state: refunds, gross: mtd?.gross, refunded: mtd?.refunded }, { compact: true });
  const todayNote = refundedNote({ state: refunds, gross: data?.today?.gross, refunded: data?.today?.refunded }, { compact: true });
  const channelNote = (c) => refundedNote({ state: refunds, gross: mtd?.grossByChannel?.[c], refunded: mtd?.refundedByChannel?.[c] });
  // "gross" is already on the scope line when refunds are unavailable; the
  // tiles only carry a subline when there is an amount to show.
  const withRefund = (text, note) => (note && !note.warn ? `${text} · ${note.text}` : text);

  return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Month to date, by channel. Same cells as the dashboard strip so the
              eye reads them the same way; different question. */}
          <div className={`kpi-scope${refunds === 'unavailable' ? ' kpi-scope-warn' : ''}`} title={labelTitle}>
            Month to date · whole business{label ? ` · ${label}` : ''}
          </div>
          <div className="kpi-strip" style={{ marginBottom: 0 }}>
            <Kpi
              label="Sales This Month" value={fmt$c(mtd.total)} tone="green" highlight
              sub={withRefund(`${mtd.orders} order${mtd.orders === 1 ? '' : 's'}`, mtdNote)}
              title={mtdNote?.title}
            />
            <Kpi
              label="Margin This Month" value={fmt$c(mtd.margin)} tone="accent" highlight
              sub={marginPct(mtd.margin, mtd.total) || 'what we kept'}
            />
            <Kpi
              label="Revenue Today" value={fmt$c(data.today?.total)} tone="green"
              // The subline is one line of ~24 characters before it ellipsises,
              // so the count is shortened when a refund has to share it.
              sub={todayNote && !todayNote.warn
                ? `${data.today?.orders ?? 0} today · ${todayNote.text}`
                : `${data.today?.orders ?? 0} activated today`}
              title={todayNote?.title}
            />
            {CHANNEL_ORDER.map(c => (
              (c !== 'Unattributed' || byChannel[c] > 0) && (
                <Kpi
                  key={c} label={CHANNEL_LABEL[c] || c} value={fmt$c(byChannel[c] || 0)}
                  tone={c === 'Unattributed' ? 'yellow' : c === 'Rep' ? 'green' : c === 'Web' ? 'accent' : undefined}
                  // Revenue on the face, margin underneath: the channel that
                  // sells the most is not always the one that earns the most,
                  // and that gap is the whole reason this page exists.
                  sub={`${fmt$c(marginByChannel[c] || 0)} margin · ${pct(byChannel[c] || 0, mtd.total)}`}
                  subTone={c === 'Unattributed' ? 'warn' : undefined}
                  // The subline is full; what came off this channel is on hover.
                  title={channelNote(c)?.title}
                />
              )
            ))}
          </div>

          <div className="analytics-cols" style={{ marginBottom: 0 }}>
            {/* Trend, stacked by channel. */}
            <div className="chart-card">
              <div className="chart-head">
                <span className="chart-title">Revenue by Channel</span>
                <span className="chart-meta" title={labelTitle}>last {data.months} months · {fmt$c(trendTotal)}{label ? ` · ${label}` : ''}</span>
              </div>
              {(data.trend || []).length === 0 || activeChannels.length === 0 ? (
                <div style={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 12 }}>No order data in this window</div>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={data.trend} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                    <CartesianGrid stroke="#2a2a38" vertical={false} />
                    <XAxis dataKey="month" tick={{ fill: '#5a5a72', fontSize: 10, fontFamily: 'DM Mono' }} axisLine={false} tickLine={false} />
                    <YAxis width={54} tick={{ fill: '#5a5a72', fontSize: 10, fontFamily: 'DM Mono' }} axisLine={false} tickLine={false} tickFormatter={axisMoney} />
                    <Tooltip content={<TrendTooltip />} cursor={{ fill: 'rgba(124,109,250,0.08)' }} />
                    <Legend wrapperStyle={{ fontFamily: 'DM Mono', fontSize: 11, color: '#9090a8' }} iconType="square" iconSize={8} />
                    {activeChannels.map((c, i) => (
                      // isAnimationActive={false}: see Dashboard — recharts measures
                      // twice inside a grid track and freezes the bars at the first
                      // width if it animates.
                      <Bar key={c} dataKey={c} stackId="rev" fill={CHANNEL_COLOR[c]} maxBarSize={72} isAnimationActive={false}
                        radius={i === activeChannels.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]} />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* By rep, month to date. This is the payroll number. */}
            <div className="table-card" style={{ marginBottom: 0 }}>
              <div className="table-header">
                <span className="table-title">By Rep · this month</span>
                <span className="table-count">{(data.byRep || []).length}</span>
              </div>
              {(data.byRep || []).length === 0 ? (
                <div style={{ padding: 22, textAlign: 'center', color: 'var(--text3)', fontSize: 12, fontFamily: 'var(--mono)', lineHeight: 1.6 }}>
                  No rep-placed orders this month.
                  <div style={{ marginTop: 4 }}>Rep-checkout and staff transfers land here; self-serve and renewals never do.</div>
                </div>
              ) : (
                <table>
                  {/* Ranked by margin, not revenue — the rep who made the
                      business the most money belongs at the top. */}
                  <thead><tr><th>Rep</th><th>Orders</th><th title={labelTitle}>{refunds === 'ok' ? 'Revenue (net)' : 'Revenue'}</th><th>Margin</th><th>Bonus ({bonusPct}%)</th></tr></thead>
                  <tbody>
                    {data.byRep.map(r => (
                      <tr key={r.userId || r.name}>
                        <td style={{ color: 'var(--text)', fontWeight: 600 }}>{r.name || '—'}</td>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text2)' }}>{r.orders}</td>
                        {/* Net of what came back on this rep's orders, with the
                            refund under it. The margin and bonus beside it were
                            already net: margin is re-stamped on refund. */}
                        <td
                          style={{ fontFamily: 'var(--mono)', color: 'var(--text2)', fontSize: 12 }}
                          title={refundedNote({ state: refunds, gross: r.grossRevenue, refunded: r.refunded })?.title}
                        >
                          {fmt$(r.revenue)}
                          {refunds === 'ok' && <RefundedLine amount={r.refunded} />}
                        </td>
                        <td style={{ fontFamily: 'var(--mono)', color: 'var(--accent3)', fontWeight: 600 }}>{fmt$(r.margin)}</td>
                        <td style={{ fontFamily: 'var(--mono)', color: 'var(--badge-yellow)', fontWeight: 700 }}>{fmt$(r.bonus)}</td>
                      </tr>
                    ))}
                    {data.byRep.length > 1 && (
                      <tr>
                        <td style={{ color: 'var(--text3)', fontFamily: 'var(--mono)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Total</td>
                        <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--text2)' }}>{data.byRep.reduce((s, r) => s + r.orders, 0)}</td>
                        <td style={{ fontFamily: 'var(--mono)', color: 'var(--text2)', fontSize: 12 }}>{fmt$(byChannel.Rep)}</td>
                        <td style={{ fontFamily: 'var(--mono)', color: 'var(--accent3)', fontWeight: 600 }}>{fmt$(marginByChannel.Rep)}</td>
                        <td style={{ fontFamily: 'var(--mono)', color: 'var(--badge-yellow)', fontWeight: 700 }}>{fmt$(repTotalBonus)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Latest orders, every channel, with who took them. */}
          <div className="table-card" style={{ marginBottom: 0 }}>
            <div className="table-header"><span className="table-title">Recent Orders</span><span className="table-count">{(data.recentOrders || []).length}</span></div>
            <table>
              <thead><tr><th>Order #</th><th>Account</th><th>Products</th><th>Channel</th><th>Placed by</th><th title={labelTitle}>{refunds === 'ok' ? 'Total (net)' : 'Total'}</th><th>Margin</th><th>Date</th></tr></thead>
              <tbody>
                {(data.recentOrders || []).length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text3)', padding: 22 }}>No orders yet</td></tr>}
                {(data.recentOrders || []).map(o => (
                  <tr key={o.Id} style={{ cursor: o.Account ? 'pointer' : 'default' }} onClick={() => o.Account && onPickCustomer?.(o.Account)}>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--accent)', fontWeight: 700 }}>{o.OrderNumber}</td>
                    <td style={{ color: 'var(--text)', fontWeight: 600 }}>{o.Account?.Name || '—'}</td>
                    <td style={{ color: 'var(--text2)', fontSize: 12 }}>{(o.products || []).join(', ') || '—'}</td>
                    <td>
                      <span className="badge" style={{ background: CHANNEL_COLOR[o.channel] + '22', color: CHANNEL_COLOR[o.channel], border: `1px solid ${CHANNEL_COLOR[o.channel]}55`, fontFamily: 'var(--mono)' }}>
                        {o.channel}
                      </span>
                    </td>
                    <td style={{ fontSize: 12, color: o.placedBy ? 'var(--text2)' : 'var(--text3)', fontFamily: o.placedBy ? 'var(--font)' : 'var(--mono)' }}>
                      {o.placedBy?.name || (o.channel === 'Renewal' ? 'auto-renewal' : o.channel === 'Web' ? 'self-serve' : '—')}
                    </td>
                    {/* What the order is worth NOW: its total less what went
                        back, with the refund under it. With refunds unavailable
                        this is the gross total and nothing claims otherwise. */}
                    <td style={{ fontFamily: 'var(--mono)', color: 'var(--text2)', fontSize: 12 }} title={orderAmount(o, refunds).title}>
                      {fmt$(orderAmount(o, refunds).amount)}
                      <RefundedLine amount={orderAmount(o, refunds).refunded} />
                    </td>
                    {/* An unstamped order shows a dash, never $0.00 — it was
                        not measured, which is not the same as earning nothing.
                        An estimated one is flagged so the number is not read as
                        a measurement. */}
                    <td style={{ fontFamily: 'var(--mono)', fontWeight: 600, color: o.margin == null ? 'var(--text3)' : 'var(--accent3)' }}>
                      {o.margin == null ? '—' : fmt$(o.margin)}
                      {o.marginBasis && o.marginBasis !== 'Actual' && (
                        <span title={`Margin ${o.marginBasis.toLowerCase()} from the family target — no sourced supplier cost`}
                          style={{ color: 'var(--badge-yellow)', marginLeft: 4, fontSize: 10 }}>~</span>
                      )}
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text3)' }}>{fmtDate(o.EffectiveDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
  );
}
